/**
 * Platform Push Notifications Abstraction
 */

import { bridge, BridgeChannels, BridgeActions } from '../bridge';
import {
  requestNotificationPermission as webRequestPermission,
  getCurrentFcmToken as webGetCurrentToken,
  onForegroundMessage as webOnForegroundMessage,
  isPushNotificationsSupported as webIsPushSupported,
} from '../firebase';

export interface PushNotification {
  title: string;
  body: string;
  data?: Record<string, any>;
}

export type PermissionStatus = 'granted' | 'denied' | 'default';

export interface PushPlatform {
  /** Request permission and return FCM token */
  requestPermission(): Promise<string | null>;
  
  /** Get existing FCM token (if permission already granted) */
  getToken(): Promise<string | null>;
  
  /** Get current permission status */
  getPermissionStatus(): Promise<PermissionStatus>;
  
  /** Listen for foreground notifications */
  onNotification(callback: (notification: PushNotification) => void): () => void;
  
  /** Check if push is supported */
  isSupported(): boolean;
  
  /** Get the platform identifier */
  getPlatformId(): 'web' | 'ios' | 'android';
}

class WebPush implements PushPlatform {
  async requestPermission(): Promise<string | null> {
    return webRequestPermission();
  }

  async getToken(): Promise<string | null> {
    return webGetCurrentToken();
  }

  async getPermissionStatus(): Promise<PermissionStatus> {
    if (typeof Notification === 'undefined') {
      return 'denied'; // No Notification API means no support
    }
    return Notification.permission as PermissionStatus;
  }

  onNotification(callback: (notification: PushNotification) => void): () => void {
    return webOnForegroundMessage((payload) => {
      callback({
        title: payload.notification?.title ?? '',
        body: payload.notification?.body ?? '',
        data: payload.data,
      });
    });
  }

  isSupported(): boolean {
    return webIsPushSupported();
  }

  getPlatformId(): 'web' | 'ios' | 'android' {
    return 'web';
  }
}

class NativePush implements PushPlatform {
  async requestPermission(): Promise<string | null> {
    try {
      return await bridge.request<string | null>(
        BridgeChannels.PUSH,
        BridgeActions.REQUEST_PERMISSION
      );
    } catch (e) {
      console.error('[NativePush] requestPermission error:', e);
      return null;
    }
  }

  async getToken(): Promise<string | null> {
    try {
      return await bridge.request<string | null>(
        BridgeChannels.PUSH,
        BridgeActions.GET_PUSH_TOKEN
      );
    } catch (e) {
      console.error('[NativePush] getToken error:', e);
      return null;
    }
  }

  async getPermissionStatus(): Promise<PermissionStatus> {
    try {
      // Try to get permission status from native
      const status = await bridge.request<PermissionStatus>(
        BridgeChannels.PUSH,
        BridgeActions.GET_PERMISSION_STATUS
      );
      return status ?? 'default';
    } catch {
      // Default to 'default' so we'll prompt
      return 'default';
    }
  }

  onNotification(callback: (notification: PushNotification) => void): () => void {
    return bridge.on(BridgeChannels.PUSH, BridgeActions.NOTIFICATION, callback);
  }

  isSupported(): boolean {
    return true; // Native always supports push
  }

  getPlatformId(): 'web' | 'ios' | 'android' {
    // Detect platform from bridge metadata
    const platform = window.OutCloseBridge?.platform?.toLowerCase();
    if (platform === 'ios') return 'ios';
    if (platform === 'android') return 'android';
    // Default to ios if unknown but native
    return 'ios';
  }
}

const webPush = new WebPush();
const nativePush = new NativePush();

export function getPushPlatform(): PushPlatform {
  return bridge.isNative ? nativePush : webPush;
}

